import { useState } from "react";
import { MessageCircle, X } from "lucide-react";

const WHATSAPP_NUMBER = "919960749074"; // +91 99607 49074 (no '+', no spaces)
const DEFAULT_MESSAGE =
  "Hi SBK Manufacturing, I'd like to enquire about your notebooks. Could you share details and pricing?";

const WhatsAppButton = () => {
  const [open, setOpen] = useState(false);
  const link = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    DEFAULT_MESSAGE
  )}`;

  return (
    <>
      {/* Tooltip / chat preview */}
      {open && (
        <div
          data-testid="whatsapp-tooltip"
          className="fixed bottom-24 right-5 md:bottom-28 md:right-8 z-[60] w-[280px] sm:w-[320px] bg-white rounded-lg shadow-2xl border border-stone-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2"
          style={{ animation: "wa-pop 0.25s ease-out" }}
        >
          <div className="bg-[#075E54] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white grid place-items-center flex-shrink-0">
                <img
                  src="/sbk-logo.png"
                  alt="SBK"
                  className="w-7 h-7 object-contain"
                />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-medium">SBK Manufacturing</div>
                <div className="text-[11px] text-white/80">
                  Typically replies in 1 hr
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="whatsapp-close"
              aria-label="Close"
              className="text-white/80 hover:text-white p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-[#ECE5DD] p-4 text-sm text-stone-800">
            <div className="bg-white rounded-md rounded-tl-none px-3 py-2 shadow-sm inline-block max-w-[90%]">
              Hi! 👋 How can we help you today? Tap below to chat with us on
              WhatsApp.
            </div>
          </div>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-start-chat"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1faa54] text-white font-medium py-3 transition-colors"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
            </svg>
            Start Chat
          </a>
        </div>
      )}

      {/* Floating Action Button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-fab"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-[60] w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] hover:bg-[#1faa54] text-white shadow-2xl shadow-emerald-900/30 grid place-items-center transition-transform hover:scale-105 active:scale-95"
        style={{
          boxShadow:
            "0 10px 25px -5px rgba(37, 211, 102, 0.45), 0 8px 10px -6px rgba(37, 211, 102, 0.3)",
        }}
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" />
        {open ? (
          <X className="w-6 h-6 relative" />
        ) : (
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="relative"
            aria-hidden="true"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
          </svg>
        )}
      </button>
    </>
  );
};

export default WhatsAppButton;
